/**
 * SVGFloorPlan - Display warehouse floor plans from converted Visio SVGs
 * with inventory overlay capabilities.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { endpoints } from '../config/api';

interface SVGFloorPlanProps {
  warehouseCode: string;
  svgUrl?: string;
  showInventory?: boolean;
  onLocationClick?: (location: LocationData) => void;
  onClose?: () => void;
}

export interface LocationData {
  id: number;
  name: string;
  quantity: number;
  product_count: number;
  fill_ratio: number;
  x?: number;
  y?: number;
}

interface QuantData {
  location: { id: number; name: string };
  quantity: number;
}

// Color scale for inventory heatmap
const getHeatmapColor = (fillRatio: number): string => {
  if (fillRatio <= 0) return 'rgba(100, 100, 100, 0.5)';
  if (fillRatio < 0.25) return 'rgba(76, 175, 80, 0.7)';   // Green
  if (fillRatio < 0.5) return 'rgba(255, 235, 59, 0.7)';   // Yellow
  if (fillRatio < 0.75) return 'rgba(255, 152, 0, 0.7)';   // Orange
  return 'rgba(244, 67, 54, 0.8)';                          // Red
};

// Known SVG file paths for warehouses
const WAREHOUSE_SVG_PATHS: Record<string, string> = {
  'IN': '/data/sites/in/converted/IN.svg',
  'MO': '/data/sites/mo/converted/MO.svg',
  'ID': '/data/sites/id/converted/ID Warehouse.svg',
  'GA-PM': '/data/sites/ga-pm/converted/GA Plant.svg',
  'GA': '/data/sites/ga-pm/converted/GA Plant.svg',
  'PA': '/data/sites/pa/converted/PA.svg',
};

export default function SVGFloorPlan({
  warehouseCode,
  svgUrl,
  showInventory = true,
  onLocationClick,
  onClose,
}: SVGFloorPlanProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [locationData, setLocationData] = useState<LocationData[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Load SVG file
  useEffect(() => {
    const loadSvg = async () => {
      setLoading(true);
      setError(null);

      const url = svgUrl || WAREHOUSE_SVG_PATHS[warehouseCode.toUpperCase()];
      if (!url) {
        setError(`No floor plan available for warehouse: ${warehouseCode}`);
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to load floor plan: ${response.statusText}`);
        }
        const text = await response.text();
        setSvgContent(text);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load floor plan');
        setLoading(false);
      }
    };

    loadSvg();
  }, [warehouseCode, svgUrl]);

  // Load inventory data
  useEffect(() => {
    if (!showInventory) return;

    const loadInventory = async () => {
      try {
        const response = await fetch(
          endpoints.odooQuants(warehouseCode, 'production'),
          { credentials: 'include' }
        );

        if (!response.ok) return;

        const data = await response.json();
        const quants: QuantData[] = data.quants || [];

        // Aggregate by location
        const locationMap = new Map<number, LocationData>();
        for (const quant of quants) {
          const locId = quant.location.id;
          const existing = locationMap.get(locId);

          if (existing) {
            existing.quantity += quant.quantity;
            existing.product_count += 1;
          } else {
            locationMap.set(locId, {
              id: locId,
              name: quant.location.name,
              quantity: quant.quantity,
              product_count: 1,
              fill_ratio: 0,
            });
          }
        }

        // Calculate fill ratios
        const locations = Array.from(locationMap.values()).map(loc => ({
          ...loc,
          fill_ratio: Math.min(loc.quantity / 100, 1),
        }));

        locations.sort((a, b) => b.quantity - a.quantity);
        setLocationData(locations);
      } catch (err) {
        console.error('Failed to load inventory:', err);
      }
    };

    loadInventory();
  }, [warehouseCode, showInventory]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setZoom(z => Math.min(z * 1.2, 5));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(z => Math.max(z / 1.2, 0.2));
  }, []);

  const handleResetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Pan handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 0) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  }, [pan]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
    }
  }, [isPanning, panStart]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(z => Math.min(Math.max(z * delta, 0.2), 5));
  }, []);

  const handleLocationSelect = useCallback((loc: LocationData) => {
    setSelectedLocation(loc);
    onLocationClick?.(loc);
  }, [onLocationClick]);

  if (loading) {
    return (
      <div className="svg-floor-plan">
        <div className="svg-loading">
          <div className="loading-spinner" />
          <p>Loading floor plan...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="svg-floor-plan">
        <div className="svg-error">
          <p>{error}</p>
          {onClose && (
            <button className="svg-btn" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="svg-floor-plan">
      {/* Header */}
      <div className="svg-header">
        <h3>
          {warehouseCode} Floor Plan
          <span className="svg-badge">{locationData.length} locations</span>
        </h3>
        <div className="svg-controls">
          <button className="svg-btn" onClick={handleZoomOut} title="Zoom Out">−</button>
          <span className="svg-zoom-level">{Math.round(zoom * 100)}%</span>
          <button className="svg-btn" onClick={handleZoomIn} title="Zoom In">+</button>
          <button className="svg-btn" onClick={handleResetView} title="Reset View">⟲</button>
          {onClose && (
            <button className="svg-close-btn" onClick={onClose}>×</button>
          )}
        </div>
      </div>

      <div className="svg-main">
        {/* SVG Container */}
        <div
          className="svg-container"
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        >
          <div
            className="svg-transform"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              cursor: isPanning ? 'grabbing' : 'grab',
            }}
          >
            {svgContent && (
              <div
                className="svg-content"
                dangerouslySetInnerHTML={{ __html: svgContent }}
              />
            )}
          </div>
        </div>

        {/* Inventory Sidebar */}
        {showInventory && (
          <div className="svg-sidebar">
            <h4>Inventory Status</h4>

            {/* Legend */}
            <div className="svg-legend">
              <div className="svg-legend-item">
                <span className="svg-legend-color" style={{ backgroundColor: 'rgba(76, 175, 80, 0.7)' }} />
                <span>Low (&lt;25%)</span>
              </div>
              <div className="svg-legend-item">
                <span className="svg-legend-color" style={{ backgroundColor: 'rgba(255, 235, 59, 0.7)' }} />
                <span>Medium (25-50%)</span>
              </div>
              <div className="svg-legend-item">
                <span className="svg-legend-color" style={{ backgroundColor: 'rgba(255, 152, 0, 0.7)' }} />
                <span>High (50-75%)</span>
              </div>
              <div className="svg-legend-item">
                <span className="svg-legend-color" style={{ backgroundColor: 'rgba(244, 67, 54, 0.8)' }} />
                <span>Full (&gt;75%)</span>
              </div>
            </div>

            {/* Stats */}
            <div className="svg-stats">
              <div className="svg-stat">
                <span className="svg-stat-value">{locationData.length}</span>
                <span className="svg-stat-label">Locations</span>
              </div>
              <div className="svg-stat">
                <span className="svg-stat-value">
                  {locationData.reduce((sum, l) => sum + l.quantity, 0).toLocaleString()}
                </span>
                <span className="svg-stat-label">Total Units</span>
              </div>
            </div>

            {/* Location List */}
            <div className="svg-location-list">
              <h5>Top Locations</h5>
              {locationData.slice(0, 20).map((loc) => (
                <div
                  key={loc.id}
                  className={`svg-location-item ${selectedLocation?.id === loc.id ? 'selected' : ''}`}
                  onClick={() => handleLocationSelect(loc)}
                >
                  <span
                    className="svg-location-indicator"
                    style={{ backgroundColor: getHeatmapColor(loc.fill_ratio) }}
                  />
                  <div className="svg-location-info">
                    <span className="svg-location-name">{loc.name}</span>
                    <span className="svg-location-detail">
                      {loc.quantity.toLocaleString()} units • {loc.product_count} SKUs
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Location */}
            {selectedLocation && (
              <div className="svg-detail-panel">
                <h5>{selectedLocation.name}</h5>
                <div className="svg-detail-row">
                  <span>Quantity:</span>
                  <span>{selectedLocation.quantity.toLocaleString()}</span>
                </div>
                <div className="svg-detail-row">
                  <span>Products:</span>
                  <span>{selectedLocation.product_count}</span>
                </div>
                <div className="svg-detail-row">
                  <span>Fill Level:</span>
                  <span>{(selectedLocation.fill_ratio * 100).toFixed(0)}%</span>
                </div>
                <div className="svg-fill-bar">
                  <div
                    className="svg-fill-bar-inner"
                    style={{
                      width: `${selectedLocation.fill_ratio * 100}%`,
                      backgroundColor: getHeatmapColor(selectedLocation.fill_ratio),
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        .svg-floor-plan {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #0a0a1a;
          color: #e0e0e0;
          font-family: system-ui, -apple-system, sans-serif;
        }

        .svg-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: #1a1a2e;
          border-bottom: 1px solid #333;
        }

        .svg-header h3 {
          margin: 0;
          font-size: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .svg-badge {
          background: #4a90d9;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: normal;
        }

        .svg-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .svg-btn {
          padding: 6px 12px;
          border: 1px solid #444;
          background: #2a2a3e;
          color: #e0e0e0;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .svg-btn:hover {
          background: #3a3a5e;
        }

        .svg-zoom-level {
          font-size: 12px;
          color: #888;
          min-width: 50px;
          text-align: center;
        }

        .svg-close-btn {
          background: transparent;
          border: none;
          color: #888;
          font-size: 24px;
          cursor: pointer;
          padding: 0 8px;
        }

        .svg-main {
          display: flex;
          flex: 1;
          overflow: hidden;
        }

        .svg-container {
          flex: 1;
          overflow: hidden;
          position: relative;
          background: #12121f;
        }

        .svg-transform {
          transform-origin: center center;
          transition: transform 0.1s ease-out;
        }

        .svg-content {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100%;
        }

        .svg-content svg {
          max-width: 100%;
          height: auto;
          background: white;
        }

        .svg-sidebar {
          width: 280px;
          background: #1a1a2e;
          border-left: 1px solid #333;
          padding: 16px;
          overflow-y: auto;
        }

        .svg-sidebar h4 {
          margin: 0 0 16px 0;
          font-size: 14px;
        }

        .svg-sidebar h5 {
          margin: 16px 0 8px 0;
          font-size: 12px;
          color: #888;
          text-transform: uppercase;
        }

        .svg-legend {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 16px;
        }

        .svg-legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          color: #aaa;
        }

        .svg-legend-color {
          width: 12px;
          height: 12px;
          border-radius: 2px;
        }

        .svg-stats {
          display: flex;
          gap: 16px;
          margin-bottom: 16px;
          padding: 12px;
          background: #2a2a3e;
          border-radius: 6px;
        }

        .svg-stat {
          display: flex;
          flex-direction: column;
        }

        .svg-stat-value {
          font-size: 18px;
          font-weight: 600;
        }

        .svg-stat-label {
          font-size: 11px;
          color: #888;
        }

        .svg-location-list {
          max-height: 300px;
          overflow-y: auto;
        }

        .svg-location-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px;
          border-radius: 4px;
          cursor: pointer;
        }

        .svg-location-item:hover {
          background: #2a2a3e;
        }

        .svg-location-item.selected {
          background: #3a3a5e;
        }

        .svg-location-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .svg-location-info {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .svg-location-name {
          font-size: 12px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .svg-location-detail {
          font-size: 10px;
          color: #888;
        }

        .svg-detail-panel {
          margin-top: 16px;
          padding: 12px;
          background: #2a2a3e;
          border-radius: 6px;
        }

        .svg-detail-panel h5 {
          margin: 0 0 12px 0;
          color: #fff;
          text-transform: none;
        }

        .svg-detail-row {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          margin-bottom: 6px;
        }

        .svg-detail-row span:first-child {
          color: #888;
        }

        .svg-fill-bar {
          height: 6px;
          background: #333;
          border-radius: 3px;
          margin-top: 8px;
          overflow: hidden;
        }

        .svg-fill-bar-inner {
          height: 100%;
        }

        .svg-loading, .svg-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          gap: 16px;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #333;
          border-top-color: #4a90d9;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
